import axios from 'axios';
import { User, PhotoGroup, Video, Icon } from '../types';
import { API_URL } from '../config/api';
import { getSessionToken } from './sessionToken';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true
});

api.interceptors.request.use((config) => {
  if (config.data instanceof FormData && config.headers) {
    delete config.headers['Content-Type'];
  }
  const token = getSessionToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, error => {
  console.error('Request error:', error);
  return Promise.reject(error);
});

// Add response interceptor for error handling
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
    }
    console.error('Response error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      config: error.config
    });
    return Promise.reject(error);
  }
);

// Auth APIs
export const register = async (userData: { username: string; email: string; password: string }) => {
  const response = await api.post<{ message: string; email: string }>('/auth/register', userData);
  return response.data;
};

export const verifyEmail = async (data: { email: string; code: string }) => {
  const response = await api.post<{ message: string; user: User }>('/auth/verify-email', data);
  return response.data;
};

export const resendVerification = async (email: string) => {
  const response = await api.post<{ message: string; email: string; emailSent: boolean; verificationCode?: string }>('/auth/resend-verification', { email });
  return response.data;
};

export const forgotPassword = async (email: string) => {
  const response = await api.post<{ message: string; email: string }>('/auth/forgot-password', { email });
  return response.data;
};

export const verifyResetCode = async (data: { email: string; code: string }) => {
  const response = await api.post<{ message: string }>('/auth/verify-reset-code', data);
  return response.data;
};

export const resetPassword = async (data: { email: string; code: string; newPassword: string }) => {
  const response = await api.post<{ message: string }>('/auth/reset-password', data);
  return response.data;
};

export const login = async (credentials: { email: string; password: string }) => {
  const response = await api.post<{ user: User; token: string }>('/auth/login', credentials);
  return response.data;
};

export const logout = async () => {
  const response = await api.post<{ ok: boolean }>('/auth/logout');
  return response.data;
};

// Photo APIs
export const uploadPhotos = async (formData: FormData) => {
  const response = await api.post<PhotoGroup>('/assets/photos', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getPhotos = async () => {
  const response = await api.get<PhotoGroup[]>('/assets/photos');
  return response.data;
};

export const deletePhoto = async (photoId: string) => {
  await api.delete(`/assets/photos/${photoId}`);
};

export interface MonoAsset {
  _id: string;
  name: string;
  url: string;
  createdAt: string;
}

export interface MonoDriveFolderInfo {
  folderId: string;
  webLink: string;
}

export interface MonoPhotoGallery {
  _id?: string;
  folderId: string;
  createdAt: string;
  viewUrl: string;
  photos: MonoAsset[];
  printCount?: number;
}

export const getMonoDriveFolder = async () => {
  const response = await api.get<MonoDriveFolderInfo>('/user/mono-drive-folder');
  return response.data;
};

export const uploadMonoPhotosWithName = async (formData: FormData) => {
  const response = await api.post<{
    folderId: string;
    viewUrl: string;
    totalPhotos: number;
    photos: MonoAsset[];
  }>('/mono-results/upload-with-name', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export interface MonoPhotoGroupFilter {
  startDate?: string;
  endDate?: string;
  sortOrder?: 'asc' | 'desc';
  machineCode?: string;
}

export const getMonoPhotoGroups = async (
  page: number = 1,
  limit: number = 10,
  filter?: MonoPhotoGroupFilter
) => {
  const response = await api.get<PaginatedResponse<MonoPhotoGallery>>('/mono-results/groups', {
    params: { page, limit, ...(filter || {}) },
  });
  return response.data;
};

export const getMonoAllGalleryIds = async () => {
  const response = await api.get<string[]>('/mono-results/all-gallery-ids');
  return response.data;
};

export const deleteMonoPhotoGroup = async (folderId: string) => {
  const response = await api.delete<{ message: string; deletedFolderId: string }>(`/mono-results/groups/${folderId}`);
  return response.data;
};

export const purgeExpiredMonoPhotoGroups = async () => {
  const response = await api.post<{ deleted: string[]; hasMore: boolean }>('/mono-results/purge-expired');
  return response.data;
};

export interface LitePhotoGallery {
  _id?: string;
  folderId: string;
  createdAt: string;
  viewUrl: string;
  photos: { name: string; url: string; createdAt?: string }[];
  printCount?: number;
}

export const getLitePhotoGroups = async (
  page: number = 1,
  limit: number = 10,
  filter?: { machineCode?: string; startDate?: string; endDate?: string }
) => {
  const response = await api.get<PaginatedResponse<LitePhotoGallery>>('/lite-results/groups', {
    params: { page, limit, ...(filter || {}) },
  });
  return response.data;
};

export const deleteLitePhotoGroup = async (folderId: string) => {
  const response = await api.delete<{ message: string; deletedFolderId: string }>(
    `/lite-results/groups/${folderId}`
  );
  return response.data;
};

export const purgeExpiredLitePhotoGroups = async () => {
  const response = await api.post<{ deleted: string[]; hasMore: boolean }>('/lite-results/purge-expired');
  return response.data;
};

// M-Photo Mono (User)
export const getMonoSubPhotos = async () => {
  const response = await api.get<MonoAsset[]>('/assets/mono/sub-photos');
  return response.data;
};

export const uploadMonoSubPhoto = async (formData: FormData) => {
  const response = await api.post<MonoAsset>('/assets/mono/sub-photos', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deleteMonoSubPhoto = async (photoId: string) => {
  const response = await api.delete(`/assets/mono/sub-photos/${photoId}`);
  return response.data;
};

export const getMonoFrames = async () => {
  const response = await api.get<MonoAsset[]>('/assets/mono/frames');
  return response.data;
};

export const uploadMonoFrame = async (formData: FormData) => {
  const response = await api.post<MonoAsset>('/assets/mono/frames', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deleteMonoFrame = async (frameId: string) => {
  const response = await api.delete(`/assets/mono/frames/${frameId}`);
  return response.data;
};

// Video APIs
export const uploadVideo = async (formData: FormData) => {
  const response = await api.post<Video>('/assets/videos', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getVideos = async () => {
  const response = await api.get<Video[]>('/assets/videos');
  return response.data;
};

export const deleteVideo = async (videoId: string) => {
  await api.delete(`/assets/videos/${videoId}`);
};

// Icon APIs
export const getIcons = async () => {
  const response = await api.get<Icon[]>('/assets/icons');
  return response.data;
};

export const uploadIcons = async (formData: FormData) => {
  const response = await api.post<Icon[]>('/assets/icons', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deleteIcon = async (iconId: string) => {
  const response = await api.delete(`/assets/icons/${iconId}`);
  return response.data;
};

export const getProIcons = async () => {
  const response = await api.get<Icon[]>('/assets/pro-icons');
  return response.data;
};

export const uploadProIcons = async (formData: FormData) => {
  const response = await api.post<Icon[]>('/assets/pro-icons', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deleteProIcon = async (iconId: string) => {
  const response = await api.delete(`/assets/pro-icons/${iconId}`);
  return response.data;
};

// Admin APIs
export interface AdminUserResponse {
  _id: string;
  username: string;
  email: string;
  role: 'USER' | 'ADMIN' | 'STAFF' | 'STAFF_USER';
  status: 'ACTIVE' | 'BLOCKED';
  mobileUploadEnabled?: boolean;
  editPageEnabled?: boolean;
  multiCaptureEnabled?: boolean;
  deviceAccess?: {
    pro: boolean;
    lite: boolean;
    mono: boolean;
  };
  /** server = multipart API; signedUrl = vé tạm PUT Firebase */
  galleryUploadMethod?: GalleryUploadMethod;
  /** Ảnh đại diện tài khoản (URL) */
  avatarUrl?: string;
  createdAt: string;
}

export interface DeviceAccessResponse {
  userId: string;
  deviceAccess: {
    pro: boolean;
    lite: boolean;
    mono: boolean;
  };
}

export interface AdminUsersListResponse {
  data: AdminUserResponse[];
  pagination: {
    total: number;
    page: number;
    totalPages: number;
    limit: number;
    hasNextPage: boolean;
  };
  stats: {
    total: number;
    active: number;
    blocked: number;
    recent: number;
  };
}

export const getAdminUsers = async (page = 1, limit = 10, search = '') => {
  const response = await api.get<AdminUsersListResponse>('/admin/users', {
    params: {
      role: 'USER',
      page,
      limit,
      ...(search ? { search } : {}),
    }
  });
  return response.data;
};

//quan cho vui :))

export const getAdminStaff = async () => {
  const response = await api.get<AdminUserResponse[]>('/admin/staff');
  return response.data;
};

export const createStaff = async (data: { username: string; email: string; password: string }) => {
  const response = await api.post<{ staff: AdminUserResponse }>('/admin/staff', data);
  return response.data;
};

export const createUser = async (data: { email: string; password: string }) => {
  const response = await api.post<{ message: string; user: AdminUserResponse }>('/admin/users', data);
  return response.data;
};

export const updateUser = async (userId: string, data: Partial<AdminUserResponse>) => {
  const response = await api.put<AdminUserResponse>(`/admin/users/${userId}`, data);
  return response.data;
};

export const toggleUserStatus = async (userId: string) => {
  const response = await api.patch<AdminUserResponse>(`/admin/users/${userId}/toggle-status`);
  return response.data;
};

export const toggleStaffStatus = async (staffId: string) => {
  const response = await api.patch<AdminUserResponse>(`/admin/staff/${staffId}/toggle-status`);
  return response.data;
};

export interface StaffUserAccount {
  _id: string;
  username: string;
  email: string;
  role: 'STAFF_USER';
  status: 'ACTIVE' | 'BLOCKED';
  ownerUserId?: string;
  createdAt: string;
  updatedAt?: string;
}

export const getMyStaffUsers = async () => {
  const response = await api.get<StaffUserAccount[]>('/staff-users');
  return response.data;
};

export const createMyStaffUser = async (data: { email: string; password: string; username?: string }) => {
  const response = await api.post<{ staff: StaffUserAccount }>('/staff-users', data);
  return response.data;
};

export const updateMyStaffUser = async (
  staffId: string,
  data: { username?: string; email?: string; password?: string; status?: 'ACTIVE' | 'BLOCKED' },
) => {
  const response = await api.put<{ staff: StaffUserAccount }>(`/staff-users/${staffId}`, data);
  return response.data;
};

export const toggleMyStaffUserStatus = async (staffId: string) => {
  const response = await api.patch<{ staff: StaffUserAccount }>(`/staff-users/${staffId}/toggle-status`);
  return response.data;
};

export const getUserStaffUsers = async (userId: string) => {
  const response = await api.get<StaffUserAccount[]>(`/staff-users/admin/users/${userId}`);
  return response.data;
};

export const createUserStaffUser = async (
  userId: string,
  data: { email: string; password: string; username?: string },
) => {
  const response = await api.post<{ staff: StaffUserAccount }>(`/staff-users/admin/users/${userId}`, data);
  return response.data;
};

export const updateUserStaffUser = async (
  userId: string,
  staffId: string,
  data: { username?: string; email?: string; password?: string; status?: 'ACTIVE' | 'BLOCKED' },
) => {
  const response = await api.put<{ staff: StaffUserAccount }>(
    `/staff-users/admin/users/${userId}/${staffId}`,
    data,
  );
  return response.data;
};

export const toggleUserStaffUserStatus = async (userId: string, staffId: string) => {
  const response = await api.patch<{ staff: StaffUserAccount }>(
    `/staff-users/admin/users/${userId}/${staffId}/toggle-status`,
  );
  return response.data;
};

export type GalleryUploadMethod = 'server' | 'signedUrl';

export type GalleryUploadMethodResponse = {
  userId: string;
  galleryUploadMethod: GalleryUploadMethod;
  galleryUploadMethodUpdatedAt?: string | null;
  firebaseAdminReady?: boolean;
  warning?: string | null;
  message?: string;
};

export const getAdminUserDeviceAccess = async (userId: string) => {
  const response = await api.get<DeviceAccessResponse>(`/admin/users/${userId}/device-access`);
  return response.data;
};

export const updateAdminUserDeviceAccess = async (
  userId: string,
  deviceAccess: { pro: boolean; lite: boolean; mono: boolean }
) => {
  const response = await api.put<DeviceAccessResponse>(`/admin/users/${userId}/device-access`, deviceAccess);
  return response.data;
};

export const getAdminUserGalleryUploadMethod = async (userId: string) => {
  const response = await api.get<GalleryUploadMethodResponse>(
    `/admin/users/${userId}/gallery-upload-method`,
  );
  return response.data;
};

export const updateAdminUserGalleryUploadMethod = async (
  userId: string,
  galleryUploadMethod: GalleryUploadMethod,
) => {
  const response = await api.put<GalleryUploadMethodResponse>(
    `/admin/users/${userId}/gallery-upload-method`,
    { galleryUploadMethod, source: 'admin' },
  );
  return response.data;
};

export type SessionChannel = 'pro' | 'lite' | 'mono';

export type SessionPolicyResponse = {
  userId: string;
  loginDurationDays: Record<SessionChannel, number | null>;
  loginDurationUpdatedAt: Record<SessionChannel, string | null>;
  forceLogoutAt: Record<SessionChannel, string | null>;
  message?: string;
  channel?: SessionChannel;
};

export const getAdminUserSessionPolicy = async (userId: string) => {
  const response = await api.get<SessionPolicyResponse>(`/admin/users/${userId}/session-policy`);
  return response.data;
};

export const updateAdminUserSessionPolicy = async (
  userId: string,
  loginDurationDays: Partial<Record<SessionChannel, number | null>>,
) => {
  const response = await api.put<SessionPolicyResponse>(`/admin/users/${userId}/session-policy`, {
    loginDurationDays,
    source: 'admin',
  });
  return response.data;
};

export const forceLogoutAdminUserChannel = async (userId: string, channel: SessionChannel) => {
  const response = await api.post<SessionPolicyResponse>(`/admin/users/${userId}/force-logout`, {
    channel,
    source: 'admin',
  });
  return response.data;
};

export const forceLogoutAdminUserMachine = async (userId: string, machineCode: string) => {
  const response = await api.post<{
    message: string;
    userId: string;
    machineCode: string;
    channel: SessionChannel | null;
    forceLogoutAt: string;
    scope: 'machine';
  }>(`/admin/users/${userId}/machines/${encodeURIComponent(machineCode)}/force-logout`, {
    source: 'admin',
  });
  return response.data;
};

export type CollectionRetentionResponse = {
  userId: string;
  collectionRetentionDays: Record<SessionChannel, number>;
  collectionRetentionUpdatedAt: Record<SessionChannel, string | null>;
  message?: string;
};

export const getAdminUserCollectionRetention = async (userId: string) => {
  const response = await api.get<CollectionRetentionResponse>(
    `/admin/users/${userId}/collection-retention`,
  );
  return response.data;
};

export const updateAdminUserCollectionRetention = async (
  userId: string,
  collectionRetentionDays: Partial<Record<SessionChannel, number>>,
) => {
  const response = await api.put<CollectionRetentionResponse>(
    `/admin/users/${userId}/collection-retention`,
    {
      collectionRetentionDays,
      source: 'admin',
    },
  );
  return response.data;
};

/** Collection retention bounds (days). Gallery standalone = days + 15. */
export const COLLECTION_RETENTION_MIN_DAYS = 14;
export const COLLECTION_RETENTION_MAX_DAYS = 150;
export const COLLECTION_RETENTION_DEFAULT_DAYS = 90;

// 🆕 Mobile Upload / Device Management feature flags
export type MobileUploadStatusPayload = {
  mobileUploadEnabled?: boolean;
  editPageEnabled?: boolean;
  multiCaptureEnabled?: boolean;
};

export const getMobileUploadStatus = async () => {
  const response = await api.get<{
    mobileUploadEnabled: boolean;
    editPageEnabled: boolean;
    multiCaptureEnabled: boolean;
    email: string;
  }>(`/users/mobile-upload-status`);
  return response.data;
};

export const updateMobileUploadStatus = async (
  userId: string,
  payload: boolean | MobileUploadStatusPayload
) => {
  const body: MobileUploadStatusPayload & { userId: string } =
    typeof payload === 'boolean'
      ? { userId, mobileUploadEnabled: payload }
      : { userId, ...payload };
  const response = await api.put<{
    message: string;
    mobileUploadEnabled: boolean;
    editPageEnabled: boolean;
    multiCaptureEnabled: boolean;
    email: string;
  }>(`/users/mobile-upload-status`, body);
  return response.data;
};

/** User: khung admin cấp + trạng thái bật Pro/Lite */
export const getMyCustomFramesChannelGranted = async (
  channel: 'pro' | 'lite',
): Promise<UserCustomFrameChannelGrantedResponse> => {
  const response = await api.get<UserCustomFrameChannelGrantedResponse>(
    `/users/custom-frames/${channel}/granted`,
  );
  return response.data;
};

export const toggleMyCustomFrameChannel = async (
  channel: 'pro' | 'lite',
  frameId: string,
  enabled: boolean,
): Promise<{ success: boolean; message: string; enabledFrameIds: string[] }> => {
  const response = await api.put<{ success: boolean; message: string; enabledFrameIds: string[] }>(
    `/users/custom-frames/${channel}/${frameId}/toggle`,
    { enabled },
  );
  return response.data;
};

export const promoteStaffToAdmin = async (userId: string) => {
  const response = await api.patch<AdminUserResponse>(`/admin/staff/${userId}/promote-to-admin`);
  return response.data;
};

// Photo Group Response interface
export interface PhotoGroupResponse {
  _id: string;
  folderId: string;
  photos: Array<{
    _id: string;
    name: string;
    url: string;
    createdAt: string;
    mediaType?: 'image' | 'video';
  }>;
  createdAt: string;
  viewUrl: string;
  printCount?: number;
}

export interface GalleryStatsDownloadItem {
  name: string;
  mediaType: 'image' | 'video';
  count: number;
}

export interface GalleryStatsItem {
  folderId: string;
  createdAt: string;
  viewUrl: string;
  visitCount: number;
  printCount: number;
  imageDownloadCount: number;
  videoDownloadCount: number;
  downloads: GalleryStatsDownloadItem[];
  coverUrl?: string | null;
  printSize?: string | null;
  machineCode?: string | null;
}

export type GalleryPrintSize = '2x4' | '4x2' | '2x6' | '6x2' | '4x6' | '6x4' | '3x4' | '4x3';

export type GalleryStatsChannel = 'pro' | 'lite' | 'mono';

export const getUserGalleryStats = async (
  channel: GalleryStatsChannel,
  opts?: { from?: string; signal?: AbortSignal }
) => {
  const path =
    channel === 'lite'
      ? '/lite-results/stats'
      : channel === 'mono'
        ? '/mono-results/stats'
        : '/results/stats';
  const response = await api.get<{ data: GalleryStatsItem[] }>(path, {
    params: opts?.from ? { from: opts.from } : undefined,
    signal: opts?.signal,
  });
  return response.data.data || [];
};

export const getAdminUserGalleryStats = async (
  userId: string,
  channel: GalleryStatsChannel,
  opts?: { from?: string; signal?: AbortSignal }
) => {
  const response = await api.get<{ data: GalleryStatsItem[] }>(
    `/admin/users/${userId}/${channel}/gallery-stats`,
    { params: opts?.from ? { from: opts.from } : undefined, signal: opts?.signal }
  );
  return response.data.data || [];
};

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  };
}

// Admin APIs for managing user content
export const getUserDetails = async (userId: string) => {
  const response = await api.get<AdminUserResponse>(`/admin/users/${userId}`);
  return response.data;
};

/** GET chỉ URL avatar — dùng khi cần refresh riêng ảnh đại diện */
export const getAdminUserAvatar = async (userId: string) => {
  const response = await api.get<{ avatarUrl: string }>(`/admin/users/${userId}/avatar`);
  return response.data;
};

/** Admin/Staff upload ảnh đại diện cho user */
export const adminUploadUserAvatar = async (userId: string, formData: FormData) => {
  const response = await api.put<{ message: string; avatarUrl: string }>(
    `/admin/users/${userId}/avatar`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

/** Fallback cho backend cũ: upload avatar qua endpoint update user */
export const adminUploadUserAvatarViaUpdateUser = async (userId: string, formData: FormData) => {
  const response = await api.put<{ message: string; user?: AdminUserResponse; avatarUrl?: string }>(
    `/admin/users/${userId}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return {
    avatarUrl: response.data.avatarUrl || response.data.user?.avatarUrl || '',
  };
};

// Admin đổi mật khẩu user (yêu cầu nhập lại mật khẩu admin để xác thực)
export const adminChangeUserPassword = async (
  userId: string,
  adminPassword: string,
  newPassword: string
) => {
  const response = await api.post<{ message: string }>(`/admin/users/${userId}/change-password`, {
    adminPassword,
    newPassword,
  });
  return response.data;
};


export const getUserPhotos = async (
  userId: string,
  page: number = 1,
  limit: number = 10,
  filter?: { machineCode?: string; startDate?: string; endDate?: string }
) => {
  const response = await api.get<PaginatedResponse<PhotoGroupResponse>>(`/admin/users/${userId}/photos`, {
    params: { page, limit, ...(filter || {}) }
  });
  return response.data;
};

export interface AdminUserCustomFrameAsset {
  _id: string;
  customFrameId: string;
  userId: string;
  name: string;
  description?: string;
  frameImage?: {
    url: string;
    path?: string;
  };
  isActive: boolean;
  tags?: string[];
  frameType?: 'user_frame' | 'background';
  metadata?: {
    originalFileName?: string;
    fileSize?: number;
    uploadDate?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface AdminUserCustomFrameAssetsResponse {
  success: boolean;
  user: {
    _id: string;
    username: string;
    email: string;
  };
  customFrame: CustomFrame;
  type: 'user_frame' | 'background';
  items: AdminUserCustomFrameAsset[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export const getAdminUserCustomFrameAssets = async (
  userId: string,
  customFrameId: string,
  type: 'user_frame' | 'background',
  page: number = 1,
  limit: number = 200,
  channel: 'pro' | 'lite' = 'pro'
) => {
  const response = await api.get<AdminUserCustomFrameAssetsResponse>(
    `/admin/users/${userId}/custom-frames/${customFrameId}/assets`,
    {
      params: { type, page, limit, channel }
    }
  );
  return response.data;
};

export const uploadAdminUserCustomFrameAssets = async (
  userId: string,
  customFrameId: string,
  type: 'user_frame' | 'background',
  files: File[],
  channel: 'pro' | 'lite' = 'pro'
) => {
  const formData = new FormData();
  const field = type === 'background' ? 'backgroundImages' : 'frameImages';
  files.forEach((file) => formData.append(field, file));
  formData.append('channel', channel);
  const pathSuffix = type === 'background' ? 'backgrounds' : 'user-frames';
  const response = await api.post<{
    success: boolean;
    message: string;
    items: AdminUserCustomFrameAsset[];
  }>(`/admin/users/${userId}/custom-frames/${customFrameId}/assets/${pathSuffix}?channel=${channel}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deleteAdminUserCustomFrameAsset = async (
  userId: string,
  customFrameId: string,
  assetId: string,
  type: 'user_frame' | 'background',
  channel: 'pro' | 'lite' = 'pro'
) => {
  const response = await api.delete<{ success: boolean; message: string }>(
    `/admin/users/${userId}/custom-frames/${customFrameId}/assets/${assetId}`,
    { params: { type, channel } }
  );
  return response.data;
};

export const adminDeleteUserPhoto = async (userId: string, folderId: string) => {
  await api.delete(`/admin/users/${userId}/photos/${folderId}`);
};

export const adminPurgeExpiredGalleries = async (
  userId: string,
  channel: 'pro' | 'lite' | 'mono'
) => {
  const response = await api.post<{ deleted: string[]; hasMore: boolean; channel: string }>(
    `/admin/users/${userId}/galleries/purge-expired`,
    { channel }
  );
  return response.data;
};

// User Media Management APIs
export const getUserIcons = async (userId: string) => {
  const response = await api.get<Icon[]>(`/admin/users/${userId}/icons`);
  return response.data;
};

export const getUserVideos = async (userId: string) => {
  const response = await api.get<Video[]>(`/admin/users/${userId}/videos`);
  return response.data;
};

export const uploadUserIcons = async (userId: string, formData: FormData) => {
  const response = await api.post<Icon[]>(`/admin/users/${userId}/icons`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const adminDeleteUserIcon = async (userId: string, iconId: string) => {
  await api.delete(`/admin/users/${userId}/icons/${iconId}`);
};

export const getUserProIcons = async (userId: string) => {
  const response = await api.get<Icon[]>(`/admin/users/${userId}/pro-icons`);
  return response.data;
};

export const uploadUserProIcons = async (userId: string, formData: FormData) => {
  const response = await api.post<Icon[]>(`/admin/users/${userId}/pro-icons`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const adminDeleteUserProIcon = async (userId: string, iconId: string) => {
  await api.delete(`/admin/users/${userId}/pro-icons/${iconId}`);
};

export const adminDeleteUserVideo = async (userId: string, videoId: string) => {
  await api.delete(`/admin/users/${userId}/videos/${videoId}`);
};

// M-Photo Mono APIs (Admin/Staff)
export const getAdminUserMonoSubPhotos = async (userId: string) => {
  const response = await api.get<MonoAsset[]>(`/admin/users/${userId}/mono/sub-photos`);
  return response.data;
};

export const adminDeleteUserMonoSubPhoto = async (userId: string, photoId: string) => {
  const response = await api.delete(`/admin/users/${userId}/mono/sub-photos/${photoId}`);
  return response.data;
};

export const getAdminUserMonoFrames = async (userId: string) => {
  const response = await api.get<MonoAsset[]>(`/admin/users/${userId}/mono/frames`);
  return response.data;
};

export const adminDeleteUserMonoFrame = async (userId: string, frameId: string) => {
  const response = await api.delete(`/admin/users/${userId}/mono/frames/${frameId}`);
  return response.data;
};

export const getAdminUserMonoDriveFolder = async (userId: string) => {
  const response = await api.get<MonoDriveFolderInfo>(`/admin/users/${userId}/mono/drive-folder`);
  return response.data;
};

export const getAdminUserMonoPhotoGroups = async (
  userId: string,
  page: number = 1,
  limit: number = 10,
  filter?: { machineCode?: string; startDate?: string; endDate?: string }
) => {
  const response = await api.get<PaginatedResponse<MonoPhotoGallery>>(`/admin/users/${userId}/mono/photos`, {
    params: { page, limit, ...(filter || {}) },
  });
  return response.data;
};

export const adminDeleteUserMonoPhotoGroup = async (userId: string, folderId: string) => {
  const response = await api.delete<{ message: string; deletedFolderId: string }>(
    `/admin/users/${userId}/mono/photos/${folderId}`
  );
  return response.data;
};

export const getAdminUserLitePhotoGroups = async (
  userId: string,
  page: number = 1,
  limit: number = 10,
  filter?: { machineCode?: string; startDate?: string; endDate?: string }
) => {
  const response = await api.get<PaginatedResponse<LitePhotoGallery>>(`/admin/users/${userId}/lite/photos`, {
    params: { page, limit, ...(filter || {}) },
  });
  return response.data;
};

export const adminDeleteUserLitePhotoGroup = async (userId: string, folderId: string) => {
  const response = await api.delete<{ message: string; deletedFolderId: string }>(
    `/admin/users/${userId}/lite/photos/${folderId}`
  );
  return response.data;
};

// User Profile Management
export const updateProfile = async (data: {
  username?: string;
  email?: string;
  currentPassword?: string;
  newPassword?: string;
}) => {
  const response = await api.put<{ user: User }>('/auth/profile', data);
  return response.data;
};

// User Profile Image Management
export const uploadProfileAvatar = async (formData: FormData) => {
  const response = await api.put<{ user: User }>('/auth/profile/avatar', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const uploadProfileBackground = async (formData: FormData) => {
  const response = await api.put<{ user: User }>('/auth/profile/background', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

// Thêm interface cho filter params
export interface PhotoGroupFilter {
  startDate?: string;
  endDate?: string;
  sortOrder?: 'asc' | 'desc';
  /** Mã máy 6 ký tự — khớp đúng 6 ký tự sau phần ngày giờ trong folderId */
  machineCode?: string;
}

// Photo Group APIs
export const getPhotoGroups = async (
  page: number = 1, 
  limit: number = 10,
  filter?: PhotoGroupFilter
): Promise<PaginatedResponse<PhotoGroupResponse>> => {
  const params = {
    page,
    limit,
    ...(filter || {}) // Thêm filter params nếu có
  };

  const response = await api.get<PaginatedResponse<PhotoGroupResponse>>('/results/groups', {
    params
  });
  return response.data;
};

export const uploadPhotoGroup = async (formData: FormData) => {
  const response = await api.post<PhotoGroupResponse>('/results/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deletePhotoGroup = async (folderId: string) => {
  await api.delete(`/results/groups/${folderId}`);
};

export const purgeExpiredPhotoGroups = async () => {
  const response = await api.post<{ deleted: string[]; hasMore: boolean }>('/results/purge-expired');
  return response.data;
};

// Admin Video APIs
export const uploadUserVideo = async (userId: string, formData: FormData) => {
  const response = await api.post<Video>(`/admin/users/${userId}/videos`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Export types
export type { Video, Icon };

export const downloadPhoto = async (url: string) => {
  const encodedUrl = encodeURIComponent(url);
  const response = await api.get(`/assets/photos/download?url=${encodedUrl}`, {
    responseType: 'blob'
  });
  return response.data;
};

// Add new function for batch download
export const batchDownloadPhotos = async (urls: string[]) => {
  try {
    console.log('Starting batch download with URLs:', urls);

    // Import JSZip dynamically
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    // Create a folder in the zip
    const photoFolder = zip.folder('photos');
    if (!photoFolder) {
      throw new Error('Failed to create zip folder');
    }

    // Download and add files to zip
    const downloadPromises = urls.map(async (url, index) => {
      try {
        // Use our server's download endpoint instead of direct Firebase URL
        const encodedUrl = encodeURIComponent(url);
        const response = await api.get(`/assets/photos/download?url=${encodedUrl}`, {
          responseType: 'arraybuffer',
          timeout: 30000 // 30 seconds timeout
        });

        // Get filename from URL
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/');
        const originalFilename = decodeURIComponent(pathParts[pathParts.length - 1].split('?')[0]);
        
        // Add file to zip with index to prevent duplicate names
        photoFolder.file(`${index + 1}_${originalFilename}`, response.data);
        
        return true;
      } catch (error) {
        console.error(`Failed to download ${url}:`, error);
        return false;
      }
    });

    // Process downloads in batches of 5 to avoid overwhelming the server
    const batchSize = 5;
    const results = [];
    
    for (let i = 0; i < downloadPromises.length; i += batchSize) {
      const batch = downloadPromises.slice(i, i + batchSize);
      const batchResults = await Promise.all(batch);
      results.push(...batchResults);
    }

    // Check if we have any successful downloads
    const successCount = results.filter(Boolean).length;
    if (successCount === 0) {
      throw new Error('No files were successfully downloaded');
    }

    // Generate zip file
    const blob = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: {
        level: 6
      }
    });

    return blob;
  } catch (error) {
    console.error('Batch download error:', error);
    throw new Error('Failed to download and zip photos');
  }
};

export const downloadGalleries = async (
  galleryIds: string[],
  onProgress?: (progress: number) => void
): Promise<Blob> => {
  const response = await api.post('/results/download-galleries', 
    { galleryIds },
    { 
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      }
    }
  );
  return response.data;
};

// Get latest photo API

// Collection interfaces
export interface Collection {
  _id: string;
  userId: string;
  title: string;
  description?: string;
  headerImage?: {
    url: string;
    path: string;
  };
  gifFrame?: {
    url: string;
    path: string;
  };
  galleries: string[];
  collectInfoEnabled?: boolean;
  createdAt: string;
  updatedAt: string;
}

/** GET /collections/:id — galleries là danh sách ResultGroup đầy đủ (không phải chỉ folderId). */
export interface CollectionDetail extends Omit<Collection, 'galleries'> {
  galleries: PhotoGroupResponse[];
}

// Collection APIs
export const getCollections = async (page: number = 1, limit: number = 10) => {
  const response = await api.get<PaginatedResponse<Collection>>(`/collections?page=${page}&limit=${limit}`);
  return response.data;
};

export const getCollectionById = async (collectionId: string): Promise<CollectionDetail> => {
  const response = await api.get<CollectionDetail>(`/collections/${collectionId}`);
  return response.data;
};

export const createCollection = async (formData: FormData) => {
  const response = await api.post<Collection>('/collections', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deleteCollection = async (collectionId: string) => {
  await api.delete(`/collections/${collectionId}`);
};

export const updateCollection = async (collectionId: string, formData: FormData) => {
  const response = await api.put<Collection>(`/collections/${collectionId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const updateCollectionCollectInfo = async (collectionId: string, collectInfoEnabled: boolean) => {
  const response = await api.patch<Collection>(`/collections/${collectionId}/collect-info`, {
    collectInfoEnabled
  });
  return response.data;
};

// Public gallery detail (dùng để lấy ảnh bìa fallback theo folderId)
export const getPublicGalleryByFolderId = async (folderId: string) => {
  const response = await api.get<PhotoGroupResponse>(`/results/public/${folderId}`);
  return response.data;
};

export const getUserPhotoGroups = async (userId: string, page: number = 1, limit: number = 10) => {
  const response = await api.get<PaginatedResponse<PhotoGroupResponse>>(`/admin/users/${userId}/photo-groups`, {
    params: { page, limit }
  });
  return response.data;
};

// Admin Collection APIs
export const getAdminUserCollections = async (userId: string, page: number = 1, limit: number = 10) => {
  const response = await api.get<PaginatedResponse<Collection>>(`/collections/admin/users/${userId}/collections`, {
    params: { page, limit }
  });
  return response.data;
};

export const getAdminUserCollectionDetail = async (userId: string, collectionId: string) => {
  const response = await api.get<Collection>(`/collections/admin/users/${userId}/collections/${collectionId}`);
  return response.data;
};

export const createAdminUserCollection = async (userId: string, formData: FormData) => {
  const response = await api.post<Collection>(`/collections/admin/users/${userId}/collections`, formData);
  return response.data;
};

export const updateAdminUserCollection = async (userId: string, collectionId: string, formData: FormData) => {
  const response = await api.put<Collection>(`/collections/admin/users/${userId}/collections/${collectionId}`, formData);
  return response.data;
};

export const deleteAdminUserCollection = async (userId: string, collectionId: string) => {
  await api.delete(`/collections/admin/users/${userId}/collections/${collectionId}`);
};

export const updateAdminUserCollectionCollectInfo = async (
  userId: string,
  collectionId: string,
  collectInfoEnabled: boolean
) => {
  const response = await api.patch<Collection>(
    `/collections/admin/users/${userId}/collections/${collectionId}/collect-info`,
    { collectInfoEnabled }
  );
  return response.data;
};

// Machine Management APIs (Admin Only)
export const getAdminMachines = async (page: number = 1, limit: number = 20, search?: string, isActive?: boolean) => {
  const params: any = { page, limit };
  if (search) params.search = search;
  if (isActive !== undefined) params.isActive = isActive;
  
  const response = await api.get<{
    success: boolean;
    machines: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }>('/machines/all', { params });
  return response.data;
};

export const updateMachineRandomCode = async (machineCode: string) => {
  const response = await api.put<{
    success: boolean;
    message: string;
    machine: {
      machineCode: string;
      randomCode: string;
      oldRandomCode: string;
      updatedAt: string;
    };
  }>(`/machines/${machineCode}`);
  return response.data;
};

// Machine APIs - User's machines
export interface MachineInfo {
  machineCode: string;
  randomCode: string;
  machineName: string;
  isActive: boolean;
  /** Cùng nguồn Page Control: app có trong socket room machine-app */
  isAppRunning?: boolean;
  /** windows | android — từ lần join gần nhất */
  appPlatform?: 'windows' | 'android' | null;
  /** lite | mono | pro — từ lần join gần nhất */
  appProduct?: 'lite' | 'mono' | 'pro' | null;
  createdAt: string;
}

export interface MachineRandomCodeResponse {
  success: boolean;
  machine: {
    machineCode: string;
    randomCode: string;
    fullCode: string; // machineCode + randomCode
    machineName: string;
    randomCodeUpdatedAt: string;
    isActive: boolean;
    isAppRunning?: boolean;
    appPlatform?: 'windows' | 'android' | null;
    appProduct?: 'lite' | 'mono' | 'pro' | null;
    /** Owner user — admin desktop detail dùng để tải ảnh Pro */
    userId?: string | null;
    controlPageUiEnabled?: boolean;
  };
}

export interface AdminMachineRandomCodeResponse {
  success: boolean;
  machine: {
    machineCode: string;
    randomCode: string;
    fullCode: string;
    machineName: string;
    randomCodeUpdatedAt: string;
    isActive: boolean;
    isAppRunning?: boolean;
    appPlatform?: 'windows' | 'android' | null;
    appProduct?: 'lite' | 'mono' | 'pro' | null;
    userId?: string | null;
    controlPageUiEnabled?: boolean;
  };
}

// GET danh sách machine của user hiện tại
export const getUserMachines = async () => {
  const response = await api.get<{
    success: boolean;
    machines: MachineInfo[];
  }>('/machines/my-machines/list');
  return response.data;
};

// GET danh sách machine của một user (ADMIN/STAFF)
export const getAdminUserMachines = async (userId: string) => {
  const response = await api.get<{
    success: boolean;
    machines: MachineInfo[];
  }>(`/machines/admin/users/${userId}/machines`);
  return response.data;
};

// GET random code của machine theo userId + machineCode (ADMIN/STAFF)
export const getAdminUserMachineRandomCode = async (userId: string, machineCode: string) => {
  const response = await api.get<AdminMachineRandomCodeResponse>(
    `/machines/admin/users/${userId}/machines/${machineCode}/random-code`
  );
  return response.data;
};

// GET random code theo machine code
export const getMachineRandomCode = async (machineCode: string) => {
  const response = await api.get<MachineRandomCodeResponse>(`/machines/${machineCode}/random-code`);
  return response.data;
};

export type StaffQrPurpose = 'mobile-upload' | 'photobooth-simple';

export type StaffQrTicketResponse = {
  success: boolean;
  machineCode: string;
  purpose: StaffQrPurpose;
  ticket: string;
  jti?: string;
  expiresInMs?: number;
  url: string;
  randomCode?: string | null;
  message?: string;
};

/** One-time unlimited QR for account owner / staff (web). */
export const mintStaffQrTicket = async (machineCode: string, purpose: StaffQrPurpose) => {
  const mc = encodeURIComponent(String(machineCode || '').trim().toUpperCase());
  const response = await api.post<StaffQrTicketResponse>(`/machines/${mc}/staff-qr-ticket`, {
    purpose,
  });
  return response.data;
};

// PATCH update machine name
export const updateMachineName = async (machineCode: string, machineName: string) => {
  const response = await api.patch<{
    success: boolean;
    message: string;
    machine: {
      machineCode: string;
      machineName: string;
      updatedAt: string;
    };
  }>(`/machines/${machineCode}/name`, { machineName });
  return response.data;
};

// DELETE machine (owner or ADMIN)
export const deleteMachine = async (machineCode: string) => {
  const response = await api.delete<{
    success: boolean;
    message: string;
  }>(`/machines/${machineCode}`);
  return response.data;
};

// Software Update APIs
export interface SoftwareUpdateInformationResponse {
  hasInformation: boolean;
  updateInformation: string;
}

export type SoftwareChannel = 'pro' | 'lite' | 'mono';

export interface SoftwareUpdateResponse {
  hasFile: boolean;
  channel?: SoftwareChannel;
  fileName?: string;
  fileUrl?: string;
  fileSize?: number;
  dateUpload?: string;
  uploadedBy?: {
    _id: string;
    username: string;
    email: string;
  };
  message?: string;
}

// GET software update (for USER and ADMIN)
export const getSoftwareUpdate = async () => {
  const response = await api.get<SoftwareUpdateResponse>('/software-update');
  return response.data;
};

export const getSoftwareUpdateByChannel = async (channel: SoftwareChannel) => {
  const response = await api.get<SoftwareUpdateResponse>(`/software-update/${channel}`);
  return response.data;
};

// GET software update (for ADMIN - with uploader info)
export const getSoftwareUpdateAdmin = async () => {
  const response = await api.get<SoftwareUpdateResponse>('/software-update/admin');
  return response.data;
};

export const getSoftwareUpdateAdminByChannel = async (channel: SoftwareChannel) => {
  const response = await api.get<SoftwareUpdateResponse>(`/software-update/admin/${channel}`);
  return response.data;
};

// POST upload software update (ADMIN only)
export const uploadSoftwareUpdate = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post<{
    message: string;
    data: {
      fileName: string;
      fileUrl: string;
      fileSize: number;
      dateUpload: string;
    };
  }>('/software-update/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const uploadSoftwareUpdateByChannel = async (channel: SoftwareChannel, file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post<{
    message: string;
    data: {
      channel: SoftwareChannel;
      fileName: string;
      fileUrl: string;
      fileSize: number;
      dateUpload: string;
    };
  }>(`/software-update/upload/${channel}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// DELETE software update (ADMIN only)
export const deleteSoftwareUpdate = async () => {
  const response = await api.delete<{ message: string }>('/software-update');
  return response.data;
};

export const deleteSoftwareUpdateByChannel = async (channel: SoftwareChannel) => {
  const response = await api.delete<{ message: string }>(`/software-update/${channel}`);
  return response.data;
};

// Software Update Information APIs
export const createSoftwareUpdateInformation = async (updateInformation: string) => {
  const response = await api.post<{ message: string; data: { updateInformation: string } }>(
    '/software-update/information',
    { updateInformation }
  );
  return response.data;
};

export const getSoftwareUpdateInformation = async () => {
  const response = await api.get<SoftwareUpdateInformationResponse>('/software-update/information');
  return response.data;
};

export const updateSoftwareUpdateInformation = async (updateInformation: string) => {
  const response = await api.put<{ message: string; data: { updateInformation: string } }>(
    '/software-update/information',
    { updateInformation }
  );
  return response.data;
};

// Custom Frame Enable APIs
export interface CustomFrame {
  _id: string;
  name: string;
  size: string;
  width: number;
  height: number;
  maxPhotos: number;
  photoPositions: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
  frameImage?: {
    url: string;
    path?: string;
  };
  createdAt?: string;
  isActive?: boolean;
  showInPublicMenu?: boolean;
}

export interface CustomFrameEnableResponse {
  success: boolean;
  enabledFrameIds: string[];
  frames: CustomFrame[];
}

export const getAdminCustomFrames = async (): Promise<CustomFrame[]> => {
  const response = await api.get<{ success: boolean; frames: CustomFrame[] }>(
    '/mobile-upload/admin/custom-frames'
  );
  return response.data.frames;
};

export interface UserCustomFrameSettingsResponse {
  success: boolean;
  frames: CustomFrame[];
  /** Khung admin đã cấp quyền (lớp trên) */
  grantedFrameIds: string[];
  enabledProIds: string[];
  enabledLiteIds: string[];
}

export interface UserCustomFrameChannelGrantedResponse {
  success: boolean;
  grantedFrameIds: string[];
  grantedFrames: CustomFrame[];
  enabledFrameIds: string[];
  enabledFrames: CustomFrame[];
}

/** Toàn bộ custom frame + trạng thái bật Pro/Lite (tab cài đặt user). */
export const getUserCustomFrameSettings = async (
  userId: string,
): Promise<UserCustomFrameSettingsResponse> => {
  const response = await api.get<UserCustomFrameSettingsResponse>(
    `/admin/users/${userId}/custom-frames/settings`,
  );
  return response.data;
};

/** Admin cấp / thu hồi quyền dùng khung (lớp trên Pro & Lite). */
export const toggleAdminCustomFrameGrant = async (
  userId: string,
  frameId: string,
  enabled: boolean,
): Promise<{
  success: boolean;
  message: string;
  grantedFrameIds: string[];
}> => {
  const response = await api.put<{
    success: boolean;
    message: string;
    grantedFrameIds: string[];
  }>(`/admin/users/${userId}/custom-frames/${frameId}/toggle`, { enabled });
  return response.data;
};

/** Khung admin đã cấp + user bật theo kênh Pro hoặc Lite. */
export const getUserCustomFramesChannelGranted = async (
  userId: string,
  channel: 'pro' | 'lite',
): Promise<UserCustomFrameChannelGrantedResponse> => {
  const response = await api.get<UserCustomFrameChannelGrantedResponse>(
    `/admin/users/${userId}/custom-frames/${channel}/granted`,
  );
  return response.data;
};

/** @deprecated — dùng toggleAdminCustomFrameGrant */
export const toggleUserCustomFrameBoth = toggleAdminCustomFrameGrant;

export const getUserCustomFramesEnabledPro = async (userId: string): Promise<CustomFrameEnableResponse> => {
  const response = await api.get<CustomFrameEnableResponse>(
    `/admin/users/${userId}/custom-frames-pro/enabled`,
  );
  return response.data;
};

export const toggleCustomFrameEnablePro = async (
  userId: string,
  frameId: string,
  enabled: boolean,
): Promise<{ success: boolean; message: string; enabledFrameIds: string[] }> => {
  const response = await api.put<{ success: boolean; message: string; enabledFrameIds: string[] }>(
    `/admin/users/${userId}/custom-frames-pro/${frameId}/toggle`,
    { enabled },
  );
  return response.data;
};

export const getUserCustomFramesEnabledLite = async (userId: string): Promise<CustomFrameEnableResponse> => {
  const response = await api.get<CustomFrameEnableResponse>(
    `/admin/users/${userId}/custom-frames-lite/enabled`,
  );
  return response.data;
};

export const toggleCustomFrameEnableLite = async (
  userId: string,
  frameId: string,
  enabled: boolean,
): Promise<{ success: boolean; message: string; enabledFrameIds: string[] }> => {
  const response = await api.put<{ success: boolean; message: string; enabledFrameIds: string[] }>(
    `/admin/users/${userId}/custom-frames-lite/${frameId}/toggle`,
    { enabled },
  );
  return response.data;
};

/** @deprecated — dùng getUserCustomFramesEnabledLite */
export const getUserCustomFramesEnabled = getUserCustomFramesEnabledLite;

/** @deprecated — dùng toggleCustomFrameEnableLite */
export const toggleCustomFrameEnable = toggleCustomFrameEnableLite;

export const getAdminPublicMenuFrames = async (): Promise<CustomFrame[]> => {
  const response = await api.get<{ success: boolean; frames: CustomFrame[] }>(
    '/frames/admin/public-menu'
  );
  return response.data.frames;
};

export const togglePublicMenuFrameVisibility = async (
  frameId: string,
  showInPublicMenu: boolean
): Promise<{ success: boolean; message: string }> => {
  const response = await api.put<{ success: boolean; message: string }>(
    `/frames/admin/public-menu/${frameId}/toggle`,
    { showInPublicMenu }
  );
  return response.data;
};

export const bulkTogglePublicMenuFrames = async (
  showInPublicMenu: boolean
): Promise<{ success: boolean; message: string; modifiedCount?: number }> => {
  const response = await api.put<{ success: boolean; message: string; modifiedCount?: number }>(
    '/frames/admin/public-menu/bulk',
    { showInPublicMenu }
  );
  return response.data;
};

export interface PublicMenuAlbum {
  _id: string;
  name: string;
  slug: string;
  coverImageUrl?: string;
  coverIconImageUrl?: string;
  coverIconPlacements?: Array<{ url: string; xPct: number; yPct: number; sizePct: number; rotateDeg?: number }>;
  backgroundImageUrl?: string;
  enabledFrameIds?: string[];
  frameConfigs?: Array<{
    frameId: string;
    mainImage?: {
      url?: string;
      path?: string;
      name?: string;
    } | null;
    backgroundImage?: {
      url?: string;
      path?: string;
      name?: string;
    } | null;
    iconImage?: {
      url?: string;
      path?: string;
      name?: string;
    } | null;
    iconPlacements?: Array<{ url: string; xPct: number; yPct: number; sizePct: number; rotateDeg?: number }>;
    sampleImages?: AlbumFrameDetailImage[];
  }>;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface AlbumFrameDetailImage {
  _id?: string;
  url: string;
  path?: string;
  name?: string;
}

export interface SharedFrameBackground {
  _id: string;
  url: string;
  path?: string;
  name?: string;
}

export interface SharedFrameIcon {
  _id: string;
  url: string;
  path?: string;
  name?: string;
}

export interface AlbumFrameDetailResponse {
  success: boolean;
  frame: {
    _id: string;
    name: string;
    size: string;
    isActive: boolean;
    frameImage?: {
      url?: string;
      path?: string;
    } | null;
  };
  config: {
    frameId: string;
    mainImage: AlbumFrameDetailImage | null;
    backgroundImage: AlbumFrameDetailImage | null;
    iconImage: AlbumFrameDetailImage | null;
    iconPlacements?: Array<{ url: string; xPct: number; yPct: number; sizePct: number; rotateDeg?: number }>;
    backgroundSource?: 'custom' | 'library';
    backgroundLibraryId?: string | null;
    iconSource?: 'custom' | 'library';
    iconLibraryId?: string | null;
    sampleImages: AlbumFrameDetailImage[];
  };
}

export const getAdminPublicMenuAlbums = async (): Promise<PublicMenuAlbum[]> => {
  const response = await api.get<{ success: boolean; albums: PublicMenuAlbum[] }>(
    '/frames/admin/public-menu/albums'
  );
  return response.data.albums;
};

export const createPublicMenuAlbum = async (data: {
  name?: string;
  slug?: string;
  coverImageUrl?: string;
  coverIconImageUrl?: string;
  coverIconPlacements?: Array<{ url: string; xPct: number; yPct: number; sizePct: number; rotateDeg?: number }>;
  backgroundImageUrl?: string;
  frameIconPlacementsByFrameId?: Record<
    string,
    Array<{ url: string; xPct: number; yPct: number; sizePct: number; rotateDeg?: number }>
  >;
  enabledFrameIds?: string[];
  isActive?: boolean;
}): Promise<{ success: boolean; message: string; album: PublicMenuAlbum }> => {
  const response = await api.post<{ success: boolean; message: string; album: PublicMenuAlbum }>(
    '/frames/admin/public-menu/albums',
    data
  );
  return response.data;
};

export const updatePublicMenuAlbum = async (
  albumId: string,
  data: Partial<{
    name: string;
    slug: string;
    coverImageUrl: string;
    coverIconImageUrl: string;
    coverIconPlacements: Array<{ url: string; xPct: number; yPct: number; sizePct: number; rotateDeg?: number }>;
    backgroundImageUrl: string;
    frameIconPlacementsByFrameId: Record<
      string,
      Array<{ url: string; xPct: number; yPct: number; sizePct: number; rotateDeg?: number }>
    >;
    enabledFrameIds: string[];
    isActive: boolean;
  }>
): Promise<{ success: boolean; message: string; album: PublicMenuAlbum }> => {
  const response = await api.put<{ success: boolean; message: string; album: PublicMenuAlbum }>(
    `/frames/admin/public-menu/albums/${albumId}`,
    data
  );
  return response.data;
};

export const toggleAlbumFrameVisibility = async (
  albumId: string,
  frameId: string,
  enabled: boolean
): Promise<{ success: boolean; message: string; enabledFrameIds: string[] }> => {
  const response = await api.put<{ success: boolean; message: string; enabledFrameIds: string[] }>(
    `/frames/admin/public-menu/albums/${albumId}/frames/${frameId}/toggle`,
    { enabled }
  );
  return response.data;
};

export const deletePublicMenuAlbum = async (
  albumId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete<{ success: boolean; message: string }>(
    `/frames/admin/public-menu/albums/${albumId}`
  );
  return response.data;
};

export const getAlbumFrameDetail = async (
  albumId: string,
  frameId: string
): Promise<AlbumFrameDetailResponse> => {
  const response = await api.get<AlbumFrameDetailResponse>(
    `/frames/admin/public-menu/albums/${albumId}/frames/${frameId}/detail`
  );
  return response.data;
};

export const uploadAlbumFrameBackgroundImage = async (
  albumId: string,
  frameId: string,
  file: File
): Promise<{ success: boolean; message: string; backgroundImage: AlbumFrameDetailImage }> => {
  const formData = new FormData();
  formData.append('image', file);
  const response = await api.post<{
    success: boolean;
    message: string;
    backgroundImage: AlbumFrameDetailImage;
  }>(`/frames/admin/public-menu/albums/${albumId}/frames/${frameId}/background-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const getSharedFrameBackgrounds = async (): Promise<SharedFrameBackground[]> => {
  const response = await api.get<{ success: boolean; backgrounds: SharedFrameBackground[] }>(
    '/frames/admin/public-menu/frame-backgrounds'
  );
  return response.data.backgrounds || [];
};

export const uploadSharedFrameBackground = async (
  file: File
): Promise<{ success: boolean; message: string; background: SharedFrameBackground }> => {
  const formData = new FormData();
  formData.append('image', file);
  const response = await api.post<{
    success: boolean;
    message: string;
    background: SharedFrameBackground;
  }>('/frames/admin/public-menu/frame-backgrounds/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const applySharedFrameBackgroundToAlbumFrame = async (
  albumId: string,
  frameId: string,
  backgroundId: string
): Promise<{ success: boolean; message: string; backgroundImage: AlbumFrameDetailImage }> => {
  const response = await api.put<{
    success: boolean;
    message: string;
    backgroundImage: AlbumFrameDetailImage;
  }>(`/frames/admin/public-menu/albums/${albumId}/frames/${frameId}/background-image/library`, {
    backgroundId,
  });
  return response.data;
};

export const deleteSharedFrameBackground = async (
  backgroundId: string
): Promise<{ success: boolean; message: string; detachedCount?: number }> => {
  const response = await api.delete<{ success: boolean; message: string; detachedCount?: number }>(
    `/frames/admin/public-menu/frame-backgrounds/${backgroundId}`
  );
  return response.data;
};

export const getSharedFrameIcons = async (): Promise<SharedFrameIcon[]> => {
  const response = await api.get<{ success: boolean; icons: SharedFrameIcon[] }>(
    '/frames/admin/public-menu/frame-icons'
  );
  return response.data.icons || [];
};

export const uploadSharedFrameIcon = async (
  file: File
): Promise<{ success: boolean; message: string; icon: SharedFrameIcon }> => {
  const formData = new FormData();
  formData.append('image', file);
  const response = await api.post<{
    success: boolean;
    message: string;
    icon: SharedFrameIcon;
  }>('/frames/admin/public-menu/frame-icons/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const applySharedFrameIconToAlbumFrame = async (
  albumId: string,
  frameId: string,
  iconId: string
): Promise<{ success: boolean; message: string; iconImage: AlbumFrameDetailImage }> => {
  const response = await api.put<{
    success: boolean;
    message: string;
    iconImage: AlbumFrameDetailImage;
  }>(`/frames/admin/public-menu/albums/${albumId}/frames/${frameId}/icon-image/library`, {
    iconId,
  });
  return response.data;
};

export const deleteSharedFrameIcon = async (
  iconId: string
): Promise<{ success: boolean; message: string; detachedCount?: number }> => {
  const response = await api.delete<{ success: boolean; message: string; detachedCount?: number }>(
    `/frames/admin/public-menu/frame-icons/${iconId}`
  );
  return response.data;
};

export const deleteAlbumFrameBackgroundImage = async (
  albumId: string,
  frameId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete<{ success: boolean; message: string }>(
    `/frames/admin/public-menu/albums/${albumId}/frames/${frameId}/background-image`
  );
  return response.data;
};

export const deleteAlbumFrameIconImage = async (
  albumId: string,
  frameId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete<{ success: boolean; message: string }>(
    `/frames/admin/public-menu/albums/${albumId}/frames/${frameId}/icon-image`
  );
  return response.data;
};

export const applySharedFrameIconToAlbumCover = async (
  albumId: string,
  iconId: string
): Promise<{ success: boolean; message: string; coverIconImageUrl: string }> => {
  const response = await api.put<{
    success: boolean;
    message: string;
    coverIconImageUrl: string;
  }>(`/frames/admin/public-menu/albums/${albumId}/cover-icon/library`, { iconId });
  return response.data;
};

export const deleteAlbumCoverIconImage = async (
  albumId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete<{ success: boolean; message: string }>(
    `/frames/admin/public-menu/albums/${albumId}/cover-icon`
  );
  return response.data;
};

export const uploadAlbumFrameSampleImages = async (
  albumId: string,
  frameId: string,
  files: File[]
): Promise<{ success: boolean; message: string; sampleImages: AlbumFrameDetailImage[] }> => {
  const formData = new FormData();
  files.forEach((file) => formData.append('images', file));
  const response = await api.post<{
    success: boolean;
    message: string;
    sampleImages: AlbumFrameDetailImage[];
  }>(`/frames/admin/public-menu/albums/${albumId}/frames/${frameId}/sample-images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deleteAlbumFrameSampleImage = async (
  albumId: string,
  frameId: string,
  sampleImageId: string
): Promise<{ success: boolean; message: string; sampleImages: AlbumFrameDetailImage[] }> => {
  const response = await api.delete<{
    success: boolean;
    message: string;
    sampleImages: AlbumFrameDetailImage[];
  }>(`/frames/admin/public-menu/albums/${albumId}/frames/${frameId}/sample-images/${sampleImageId}`);
  return response.data;
};

export const uploadPublicMenuAlbumImage = async (
  file: File,
  type: 'cover' | 'background'
): Promise<{ success: boolean; message: string; image: { type: string; url: string; path: string; name: string } }> => {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('type', type);
  const response = await api.post<{
    success: boolean;
    message: string;
    image: { type: string; url: string; path: string; name: string };
  }>('/frames/admin/public-menu/albums/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// Send admin email
export const sendAdminEmail = async (data: {
  to?: string;
  recipients?: string[];
  subject: string;
  html: string;
}): Promise<{ message: string; campaignId?: string; stats?: { total: number; sent: number; failed: number } }> => {
  const response = await api.post<{ message: string; campaignId?: string; stats?: { total: number; sent: number; failed: number } }>(
    '/admin/send-email',
    data
  );
  return response.data;
};

export interface EmailCampaignItem {
  _id: string;
  subject: string;
  stats: { total: number; sent: number; failed: number };
  sentBy: string;
  sentByEmail?: string | null;
  ip?: string | null;
  userAgent?: string | null;
  createdAt: string;
  updatedAt: string;
  html?: string;
}

export interface EmailCampaignDetail extends EmailCampaignItem {
  html: string;
  recipients: Array<{
    email: string;
    status: 'SENT' | 'FAILED';
    errorMessage?: string | null;
    sentAt?: string | null;
  }>;
}

export const getEmailCampaigns = async (params?: {
  page?: number;
  limit?: number;
  q?: string;
}): Promise<{
  data: EmailCampaignItem[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
}> => {
  const response = await api.get<{
    data: EmailCampaignItem[];
    pagination: { total: number; page: number; limit: number; totalPages: number };
  }>('/admin/email-campaigns', { params });
  return response.data;
};

export const getEmailCampaignDetail = async (id: string): Promise<EmailCampaignDetail> => {
  const response = await api.get<EmailCampaignDetail>(`/admin/email-campaigns/${id}`);
  return response.data;
};

export const deleteEmailCampaigns = async (ids: string[]): Promise<{ message: string; deletedCount: number }> => {
  const response = await api.delete<{ message: string; deletedCount: number }>('/admin/email-campaigns', {
    data: { ids },
  });
  return response.data;
};

export interface EmailLogItem {
  _id: string;
  to: string;
  subject: string;
  status: 'SENT' | 'FAILED';
  errorMessage?: string | null;
  sentBy: string;
  sentByEmail?: string | null;
  ip?: string | null;
  userAgent?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const getEmailLogs = async (params?: {
  page?: number;
  limit?: number;
  status?: 'SENT' | 'FAILED';
  to?: string;
  q?: string;
}): Promise<{
  data: EmailLogItem[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
}> => {
  const response = await api.get<{
    data: EmailLogItem[];
    pagination: { total: number; page: number; limit: number; totalPages: number };
  }>('/admin/email-logs', { params });
  return response.data;
};

export const getEmailLogDetail = async (id: string): Promise<EmailLogItem & { html: string }> => {
  const response = await api.get<EmailLogItem & { html: string }>(`/admin/email-logs/${id}`);
  return response.data;
};


// —— SEPAY (USER) ——

export interface SePayBankInfo {
  bankCode: string;
  accountNumber: string;
  accountName: string;
  bankShortName: string;
}

export interface SePaySettingsResponse {
  configured: boolean;
  paymentGatewayConfigured?: boolean;
  sePayUsePaymentGateway?: boolean;
  sePayPgEnv?: 'production' | 'sandbox';
  masked: { merchantId: string; secretKey: string } | null;
  maskedPg?: { merchantId: string; pgSecretKey: string } | null;
  bankInfo: SePayBankInfo | null;
  invoicePrefix: string;
  /** Giá 1 tấm 4×6 (VND); null = chưa đặt */
  price4x6Vnd: number | null;
  /** Bật = mobile upload Lite bắt buộc QR SePay (mặc định true nếu thiếu trên server) */
  sePayMobileLiteRequireCheckout?: boolean;
  /** Bật = mobile upload Pro bắt buộc QR SePay */
  sePayMobileProRequireCheckout?: boolean;
}

export const getSePaySettings = async (): Promise<SePaySettingsResponse> => {
  const response = await api.get<SePaySettingsResponse>('/sepay/settings');
  return response.data;
};

/** Xác nhận mật khẩu đăng nhập → trả API Token (và các field SePay khác nếu server trả) để hiển thị trong ~5 phút. */
export const revealSePaySettings = async (password: string): Promise<{
  merchantId: string;
  secretKey: string;
  sePayPgSecretKey: string;
}> => {
  const response = await api.post<{ merchantId: string; secretKey: string; sePayPgSecretKey: string }>(
    '/sepay/reveal-settings',
    { password },
  );
  return response.data;
};

export const putSePaySettings = async (payload: {
  merchantId?: string;
  secretKey: string;
  sePayUsePaymentGateway?: boolean;
  sePayPgEnv?: 'production' | 'sandbox';
  sePayPgSecretKey?: string;
}): Promise<{
  message: string;
  paymentGatewayConfigured?: boolean;
  sePayUsePaymentGateway?: boolean;
  sePayPgEnv?: 'production' | 'sandbox';
  masked: SePaySettingsResponse['masked'];
  bankInfo: SePayBankInfo | null;
  bankFetchError?: string;
}> => {
  const response = await api.put<{
    message: string;
    paymentGatewayConfigured?: boolean;
    sePayUsePaymentGateway?: boolean;
    sePayPgEnv?: 'production' | 'sandbox';
    masked: SePaySettingsResponse['masked'];
    bankInfo: SePayBankInfo | null;
    bankFetchError?: string;
  }>(
    '/sepay/settings',
    payload,
  );
  return response.data;
};

export const putSePayPrice4x6 = async (price4x6Vnd: number | null): Promise<{ message: string; price4x6Vnd: number | null }> => {
  const response = await api.put<{ message: string; price4x6Vnd: number | null }>('/sepay/price-4x6', { price4x6Vnd });
  return response.data;
};

export const putSePayMobileCheckoutFlags = async (payload: {
  sePayMobileLiteRequireCheckout?: boolean;
  sePayMobileProRequireCheckout?: boolean;
}): Promise<{
  message: string;
  sePayMobileLiteRequireCheckout: boolean;
  sePayMobileProRequireCheckout: boolean;
}> => {
  const response = await api.put<{
    message: string;
    sePayMobileLiteRequireCheckout: boolean;
    sePayMobileProRequireCheckout: boolean;
  }>('/sepay/mobile-checkout-flags', payload);
  return response.data;
};

export const createSePayCheckout = async (payload: {
  amount: number;
  description: string;
}): Promise<{
  orderInvoiceNumber: string;
  checkoutUrl: string;
  checkoutMode?: 'qr' | 'sepay_pg';
  checkoutFormAction?: string;
  checkoutFormFields?: Record<string, string>;
  formFields: Record<string, string>;
}> => {
  const response = await api.post<{
    orderInvoiceNumber: string;
    checkoutUrl: string;
    checkoutMode?: 'qr' | 'sepay_pg';
    checkoutFormAction?: string;
    checkoutFormFields?: Record<string, string>;
    formFields: Record<string, string>;
  }>('/sepay/create-checkout', payload);
  return response.data;
};

export interface SePayTransactionRow {
  _id: string;
  orderInvoiceNumber: string;
  amount: number;
  description: string;
  status: string;
  checkoutUrl?: string;
  sePayOrderId?: string;
  paymentMethod?: string;
  transactionId?: string;
  receivingBankName?: string;
  receivingAccountNumber?: string;
  ipnNotificationType?: string;
  paidAt?: string;
  createdAt: string;
}

export interface SePayStaleCleanupResult {
  deletedCount: number;
  orderInvoiceNumbers: string[];
  /** true = đã chạy trong 24h gần đây (server), không xóa lại */
  skipped?: boolean;
  reason?: string;
}

export const cleanupStaleSePayTransactions = async (): Promise<SePayStaleCleanupResult> => {
  const response = await api.post<SePayStaleCleanupResult>('/sepay/transactions/cleanup-stale', {});
  return response.data;
};

export const getSePayTransactions = async (): Promise<SePayTransactionRow[]> => {
  const response = await api.get<SePayTransactionRow[]>('/sepay/transactions');
  return response.data;
};

export const updateSePayInvoicePrefix = async (invoicePrefix: string): Promise<{
  message: string; invoicePrefix: string; example: string;
}> => {
  const response = await api.put<{ message: string; invoicePrefix: string; example: string }>(
    '/sepay/invoice-prefix',
    { invoicePrefix },
  );
  return response.data;
};

export const syncSePayTransactions = async (): Promise<{
  message: string;
  updated: number;
  sePayTransactionCount: number;
}> => {
  const response = await api.post<{ message: string; updated: number; sePayTransactionCount: number }>(
    '/sepay/sync-transactions',
    {},
  );
  return response.data;
};

export const getSePayWebhookUrl = async (): Promise<{ webhookUrl: string }> => {
  const response = await api.get<{ webhookUrl: string }>('/sepay/webhook-url');
  return response.data;
};

export const fetchSePayBankInfo = async (): Promise<{ message: string; bankInfo: SePayBankInfo }> => {
  const response = await api.post<{ message: string; bankInfo: SePayBankInfo }>('/sepay/fetch-bank-info', {});
  return response.data;
};

export const putSePayBankInfo = async (payload: {
  bankShortName: string;
  accountNumber: string;
  accountName?: string;
  bankCode?: string;
}): Promise<{ message: string; bankInfo: SePayBankInfo }> => {
  const response = await api.put<{ message: string; bankInfo: SePayBankInfo }>('/sepay/bank-info', payload);
  return response.data;
};

export interface SePayBankAccountItem {
  id: string;
  account_number: string;
  account_holder_name: string;
  bank_short_name: string;
  bank_full_name: string;
  bank_bin: string;
  bank_code: string;
  active: string;
}

export interface SePayVirtualAccountItem {
  id: string | number;
  account_number: string;
  account_holder_name?: string;
  bank_short_name?: string;
  status?: string;
  [key: string]: unknown;
}

export const getSePayAccountsWithVA = async (): Promise<{
  bankAccounts: SePayBankAccountItem[];
  virtualAccounts: SePayVirtualAccountItem[];
}> => {
  const response = await api.get<{
    bankAccounts: SePayBankAccountItem[];
    virtualAccounts: SePayVirtualAccountItem[];
  }>('/sepay/accounts-with-va');
  return response.data;
};

// —— SEPAY ADMIN APIs ——

export interface AdminSePayBankInfo {
  accountNumber: string;
  accountName: string;
  bankShortName: string;
}

export interface AdminUserSePaySettingsResponse {
  sePayEnabled: boolean;
  configured: boolean;
  paymentGatewayConfigured?: boolean;
  sePayUsePaymentGateway?: boolean;
  sePayPgEnv?: 'production' | 'sandbox';
  masked: { secretKey: string } | null;
  maskedPg?: { merchantId: string; pgSecretKey: string } | null;
  bankInfo: AdminSePayBankInfo | null;
  invoicePrefix: string;
  price4x6Vnd: number | null;
  sePayMobileLiteRequireCheckout?: boolean;
  sePayMobileProRequireCheckout?: boolean;
}

export const getAdminUserSePaySettings = async (userId: string): Promise<AdminUserSePaySettingsResponse> => {
  const response = await api.get<AdminUserSePaySettingsResponse>(`/admin/users/${userId}/sepay/settings`);
  return response.data;
};

export const revealAdminUserSePaySettings = async (userId: string, adminPassword: string): Promise<{
  secretKey: string;
  merchantId: string;
  sePayPgSecretKey: string;
  invoicePrefix: string;
  bankInfo: AdminSePayBankInfo | null;
}> => {
  const response = await api.post<{
    secretKey: string;
    merchantId: string;
    sePayPgSecretKey: string;
    invoicePrefix: string;
    bankInfo: AdminSePayBankInfo | null;
  }>(
    `/admin/users/${userId}/sepay/reveal-settings`,
    { adminPassword },
  );
  return response.data;
};

export const putAdminUserSePaySettings = async (
  userId: string,
  adminPassword: string,
  payload: {
    secretKey?: string;
    invoicePrefix?: string;
    merchantId?: string;
    sePayPgSecretKey?: string;
    sePayUsePaymentGateway?: boolean;
    sePayPgEnv?: 'production' | 'sandbox';
  },
): Promise<{ message: string; masked?: { secretKey: string }; invoicePrefix: string; paymentGatewayConfigured?: boolean }> => {
  const response = await api.put<{
    message: string;
    masked?: { secretKey: string };
    invoicePrefix: string;
    paymentGatewayConfigured?: boolean;
  }>(
    `/admin/users/${userId}/sepay/settings`,
    { adminPassword, ...payload },
  );
  return response.data;
};

export const putAdminUserSePayPrice4x6 = async (
  userId: string,
  adminPassword: string,
  price4x6Vnd: number | null,
): Promise<{ message: string; price4x6Vnd: number | null }> => {
  const response = await api.put<{ message: string; price4x6Vnd: number | null }>(
    `/admin/users/${userId}/sepay/price-4x6`,
    { adminPassword, price4x6Vnd },
  );
  return response.data;
};

export const toggleAdminUserSePayEnabled = async (userId: string, enabled: boolean) => {
  const response = await api.put<{ message: string; userId: string; sePayEnabled: boolean }>(
    `/admin/users/${userId}/sepay/enabled`,
    { enabled },
  );
  return response.data;
};

export const putAdminUserSePayMobileCheckoutFlags = async (
  userId: string,
  payload: { sePayMobileLiteRequireCheckout?: boolean; sePayMobileProRequireCheckout?: boolean },
): Promise<{
  message: string;
  sePayMobileLiteRequireCheckout: boolean;
  sePayMobileProRequireCheckout: boolean;
}> => {
  const response = await api.put<{
    message: string;
    sePayMobileLiteRequireCheckout: boolean;
    sePayMobileProRequireCheckout: boolean;
  }>(`/admin/users/${userId}/sepay/mobile-checkout-flags`, payload);
  return response.data;
};

export const cleanupAdminUserStaleSePayTransactions = async (userId: string): Promise<SePayStaleCleanupResult> => {
  const response = await api.post<SePayStaleCleanupResult>(
    `/admin/users/${userId}/sepay/transactions/cleanup-stale`,
    {},
  );
  return response.data;
};

export const getAdminUserSePayTransactions = async (userId: string, limit = 50): Promise<SePayTransactionRow[]> => {
  const response = await api.get<SePayTransactionRow[]>(`/admin/users/${userId}/sepay/transactions`, { params: { limit } });
  return response.data;
};

export const adminCreateSePayCheckout = async (
  userId: string,
  payload: { amount: number; description: string },
): Promise<{
  orderInvoiceNumber: string;
  checkoutUrl: string;
  amount: number;
  content: string;
  accountNumber: string;
  accountName: string;
  bankShortName: string;
}> => {
  const response = await api.post(`/admin/users/${userId}/sepay/create-checkout`, payload);
  return response.data;
};

/** Landing page công khai (HTML backend) — config JSON */
export const getLandingPageAdminConfig = async (): Promise<{ success: boolean; config: Record<string, unknown> }> => {
  const response = await api.get<{ success: boolean; config: Record<string, unknown> }>('/landing-page/admin/config');
  return response.data;
};

export const updateLandingPageConfig = async (
  config: Record<string, unknown>,
): Promise<{ success: boolean; config: Record<string, unknown> }> => {
  const response = await api.put<{ success: boolean; config: Record<string, unknown> }>('/landing-page/admin/config', {
    config,
  });
  return response.data;
};

export const uploadLandingPageAsset = async (
  file: File,
): Promise<{ success: boolean; url: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post<{ success: boolean; url: string }>(
    '/landing-page/admin/upload',
    formData,
  );
  return response.data;
};

export type LandingMediaLibraryKind = 'image' | 'video' | 'gif';

export type LandingMediaLibraryItem = {
  url: string;
  filename: string;
  kind: LandingMediaLibraryKind;
  uploadedAt: number;
};

export const getLandingPageMediaLibrary = async (): Promise<{
  success: boolean;
  items: LandingMediaLibraryItem[];
}> => {
  const response = await api.get<{ success: boolean; items: LandingMediaLibraryItem[] }>(
    '/landing-page/admin/media-library',
  );
  return response.data;
};

export const deleteLandingPageLibraryItem = async (
  filename: string,
): Promise<{ success: boolean; error?: string }> => {
  const response = await api.delete<{ success: boolean; error?: string }>(
    '/landing-page/admin/media-library',
    { params: { filename } },
  );
  return response.data;
};

export type BackgroundAiPreset = {
  id: string;
  themeId?: string;
  name: string;
  nameLabels?: BackgroundAiTagLabels;
  prompt?: string;
  tags?: string[];
  imageUrl: string;
  referenceImageUrl?: string;
  isActive?: boolean;
  sortOrder?: number;
  createdAt?: string;
  updatedAt?: string;
};

export type BackgroundAiTagLabels = { vi: string; en: string; ko: string; zh: string };

export type BackgroundAiTag = {
  key: string;
  custom?: boolean;
  labels: BackgroundAiTagLabels;
};

export type BackgroundAiTheme = {
  id: string;
  userId?: string;
  name: string;
  nameLabels?: BackgroundAiTagLabels;
  description: string;
  imageUrl: string;
  proEnabled?: boolean;
  liteEnabled?: boolean;
  proMachineCodes?: string[];
  liteMachineCodes?: string[];
  scheduleEnabled?: boolean;
  availableFrom?: string | null;
  availableUntil?: string | null;
  presets?: BackgroundAiPreset[];
  createdAt?: string;
  updatedAt?: string;
};

export type BackgroundAiAdminUser = {
  _id: string;
  username: string;
  email: string;
  avatarUrl?: string;
  status: string;
  backgroundAiEnabled: boolean;
  backgroundAiMultiSlotEnabled?: boolean;
  backgroundAiProvider?: '' | BackgroundAiProviderId;
  backgroundAiQuality?: '' | BackgroundAiQuality;
  backgroundAiFalModel?: string;
  backgroundAiPiapiModel?: string;
  quotaLimit?: number | null;
  quotaUsed?: number;
  quotaRemaining?: number | null;
  generationCount?: number;
  createdAt?: string;
};

export type BackgroundAiProviderId = 'piapi' | 'fal';
export type BackgroundAiQuality = 'low' | 'medium' | 'high' | 'auto';

export type BackgroundAiProviderInfo = {
  id: BackgroundAiProviderId;
  name: string;
  authType: string;
  defaultBase: string;
  defaultModel: string;
};

export type BackgroundAiProviderConfig = {
  configured: boolean;
  apiKeyMasked: string;
  apiKeyLength?: number;
  apiBaseUrl: string;
  model: string;
};

export type BackgroundAiProviderState = {
  provider: BackgroundAiProviderId;
  providers: BackgroundAiProviderInfo[];
  configured: boolean;
  quality: BackgroundAiQuality;
  piapi: BackgroundAiProviderConfig;
  fal: BackgroundAiProviderConfig;
};

export const getBackgroundAiAdminPresets = async () => {
  const response = await api.get<{
    success: boolean;
    configured: boolean;
    provider?: BackgroundAiProviderState;
    presets: BackgroundAiPreset[];
  }>('/background-ai/admin/presets');
  return response.data;
};

export const getBackgroundAiAdminCatalog = async (
  provider: BackgroundAiProviderId,
  quality: BackgroundAiQuality = 'low'
) => {
  const response = await api.get<{
    success: boolean;
    provider: BackgroundAiProviderId;
    providerName: string;
    quality: string;
    models: Array<{
      id: string;
      name: string;
      category?: string;
      capabilities?: string[];
      credits?: number | null;
      creditsLabel?: string;
      priceLabel?: string;
      priceByQuality?: Partial<Record<BackgroundAiQuality, string>>;
      note?: string;
    }>;
    balance?: number | null;
    usd?: number | null;
    error?: string;
  }>('/background-ai/admin/catalog', { params: { provider, quality } });
  return response.data;
};

export const getFalAccountBalance = async () => {
  const response = await api.get<{
    success: boolean;
    balance: number | null;
    currency: string;
    error?: string;
  }>('/background-ai/admin/provider/fal-balance');
  return response.data;
};

export const getBackgroundAiAdminProvider = async () => {
  const response = await api.get<{ success: boolean } & BackgroundAiProviderState>(
    '/background-ai/admin/provider'
  );
  return response.data;
};

export const updateBackgroundAiAdminProvider = async (data: {
  provider?: BackgroundAiProviderId;
  quality?: BackgroundAiQuality;
  piapiApiKey?: string;
  piapiBaseUrl?: string;
  piapiModel?: string;
  falApiKey?: string;
  falBaseUrl?: string;
  falModel?: string;
}) => {
  const response = await api.put<{ success: boolean } & BackgroundAiProviderState>(
    '/background-ai/admin/provider',
    data
  );
  return response.data;
};

export const revealBackgroundAiAdminKeys = async (password: string) => {
  const response = await api.post<{
    success: boolean;
    piapiApiKey: string;
    falApiKey: string;
    expiresInMs: number;
  }>('/background-ai/admin/reveal-keys', { password, adminPassword: password });
  return response.data;
};

export const createBackgroundAiPreset = async (data: {
  name: string;
  prompt: string;
  image?: File;
}) => {
  const formData = new FormData();
  formData.append('name', data.name);
  formData.append('prompt', data.prompt);
  if (data.image) formData.append('image', data.image);
  const response = await api.post<{ success: boolean; preset: BackgroundAiPreset }>(
    '/background-ai/admin/presets',
    formData,
  );
  return response.data;
};

export const updateBackgroundAiPreset = async (
  id: string,
  data: { name: string; prompt: string; tags?: string[]; image?: File },
) => {
  const formData = new FormData();
  formData.append('name', data.name);
  formData.append('prompt', data.prompt);
  if (data.image) formData.append('image', data.image);
  const response = await api.put<{ success: boolean; preset: BackgroundAiPreset }>(
    `/background-ai/admin/presets/${id}`,
    formData,
  );
  return response.data;
};

export const deleteBackgroundAiPreset = async (id: string) => {
  const response = await api.delete<{ success: boolean }>(`/background-ai/admin/presets/${id}`);
  return response.data;
};

export const getBackgroundAiAdminUsers = async (page = 1, limit = 50, search = '') => {
  const response = await api.get<{
    success: boolean;
    data: BackgroundAiAdminUser[];
    pagination: { total: number; page: number; limit: number; totalPages: number };
  }>('/background-ai/admin/users', {
    params: { page, limit, ...(search ? { search } : {}) },
  });
  return response.data;
};

export type BackgroundAiUsageGeneration = {
  resultUrl: string;
  resultUrls?: string[];
  imageCount?: number;
  quality: string;
  imageSize: string;
  requestIds?: string[];
  themeId?: string;
  presetId?: string;
  themeName?: string;
  presetName?: string;
  themeLabels?: BackgroundAiTagLabels;
  presetLabels?: BackgroundAiTagLabels;
  costUsd?: number | null;
  createdAt?: string;
};

export type BackgroundAiUsageMachine = {
  machineCode: string;
  machineName?: string;
  appProduct?: string;
  total: number;
  costUsd?: number | null;
  generations?: BackgroundAiUsageGeneration[];
};

export const purgeBackgroundAiAdminUsage = async () => {
  const response = await api.post<{ success: boolean; modified: number; removedAccounts: number }>(
    '/background-ai/admin/usage/purge',
  );
  return response.data;
};

export const getMyBackgroundAiUsage = async () => {
  const response = await api.get<{
    success: boolean;
    machines: BackgroundAiUsageMachine[];
    quotaLimit: number | null;
    quotaUsed: number;
    quotaRemaining: number | null;
  }>(
    '/background-ai/usage',
  );
  return response.data;
};

export const getMyBackgroundAiUsageCosts = async () => {
  const response = await api.get<{
    success: boolean;
    machines: Array<{ machineCode: string; costUsd: number }>;
    requestCosts: Record<string, number>;
  }>('/background-ai/usage/costs');
  return response.data;
};

export const getMyBackgroundAiUsagePage = async (machineCode: string, skip: number) => {
  const response = await api.get<{
    success: boolean;
    machineCode: string;
    total: number;
    generations: BackgroundAiUsageGeneration[];
  }>(`/background-ai/usage/${encodeURIComponent(machineCode)}`, { params: { skip } });
  return response.data;
};

export const getBackgroundAiAdminAllUsageCosts = async () => {
  const response = await api.get<{ success: boolean; points: { t: number; userId: string; name: string; avatarUrl?: string; costUsd: number }[] }>(
    '/background-ai/admin/usage/costs',
  );
  return response.data;
};

export const getBackgroundAiAdminUsage = async (userId: string) => {
  const response = await api.get<{ success: boolean; machines: BackgroundAiUsageMachine[] }>(
    `/background-ai/admin/users/${userId}/usage`,
  );
  return response.data;
};

export type BackgroundAiUsageMoneyPoint = {
  t: number;
  machineCode: string;
  costUsd: number;
};

export const getBackgroundAiAdminUsageCosts = async (userId: string) => {
  const response = await api.get<{
    success: boolean;
    machines: Array<{ machineCode: string; costUsd: number }>;
    requestCosts: Record<string, number>;
    points?: BackgroundAiUsageMoneyPoint[];
  }>(`/background-ai/admin/users/${userId}/usage/costs`);
  return response.data;
};

export type BackgroundAiUsageSubjectPoint = {
  t: number;
  machineCode: string;
  themeId?: string;
  presetId?: string;
  themeName?: string;
  presetName?: string;
  themeLabels?: BackgroundAiTagLabels;
  presetLabels?: BackgroundAiTagLabels;
};

export const getBackgroundAiAdminUsageSubjects = async (userId: string) => {
  const response = await api.get<{ success: boolean; points: BackgroundAiUsageSubjectPoint[] }>(
    `/background-ai/admin/users/${userId}/usage/subjects`,
  );
  return response.data;
};

export const getBackgroundAiAdminUsagePage = async (
  userId: string,
  machineCode: string,
  skip: number,
) => {
  const response = await api.get<{
    success: boolean;
    machineCode: string;
    total: number;
    generations: BackgroundAiUsageGeneration[];
  }>(
    `/background-ai/admin/users/${userId}/usage/${encodeURIComponent(machineCode)}`,
    { params: { skip } },
  );
  return response.data;
};

const themeForm = (data: {
  name: string;
  description?: string;
  image?: File;
  proEnabled?: boolean;
  liteEnabled?: boolean;
  proMachineCodes?: string[];
  liteMachineCodes?: string[];
  scheduleEnabled?: boolean;
  availableFrom?: string | null;
  availableUntil?: string | null;
  sourceLang?: string;
  nameLabels?: BackgroundAiTagLabels;
}) => {
  const formData = new FormData();
  formData.append('name', data.name);
  if (data.sourceLang) formData.append('sourceLang', data.sourceLang);
  if (data.nameLabels) formData.append('nameLabels', JSON.stringify(data.nameLabels));
  if (data.description !== undefined) formData.append('description', data.description);
  if (data.proEnabled !== undefined) formData.append('proEnabled', data.proEnabled ? 'true' : 'false');
  if (data.liteEnabled !== undefined) formData.append('liteEnabled', data.liteEnabled ? 'true' : 'false');
  if (data.proMachineCodes !== undefined) formData.append('proMachineCodes', JSON.stringify(data.proMachineCodes));
  if (data.liteMachineCodes !== undefined) formData.append('liteMachineCodes', JSON.stringify(data.liteMachineCodes));
  if (data.scheduleEnabled !== undefined) formData.append('scheduleEnabled', data.scheduleEnabled ? 'true' : 'false');
  if (data.availableFrom !== undefined) formData.append('availableFrom', data.availableFrom || '');
  if (data.availableUntil !== undefined) formData.append('availableUntil', data.availableUntil || '');
  if (data.image) formData.append('image', data.image);
  return formData;
};

const presetForm = (data: {
  name: string;
  sourceLang?: string;
  nameLabels?: BackgroundAiTagLabels;
  prompt: string;
  tags?: string[];
  image?: File;
  referenceImage?: File;
  clearReference?: boolean;
}) => {
  const formData = new FormData();
  formData.append('name', data.name);
  if (data.sourceLang) formData.append('sourceLang', data.sourceLang);
  if (data.nameLabels) formData.append('nameLabels', JSON.stringify(data.nameLabels));
  formData.append('prompt', data.prompt);
  formData.append('tags', JSON.stringify(data.tags || []));
  if (data.image) formData.append('image', data.image);
  if (data.referenceImage) formData.append('referenceImage', data.referenceImage);
  if (data.clearReference) formData.append('clearReference', 'true');
  return formData;
};

export const getBackgroundAiStatus = async () => {
  const response = await api.get<{ success: boolean; enabled: boolean }>('/background-ai/status');
  return response.data;
};

export const getMyBackgroundAiTags = async () => {
  const response = await api.get<{ success: boolean; tags: BackgroundAiTag[] }>('/background-ai/tags');
  return response.data;
};

export const translateBackgroundAiName = async (sourceLang: string, text: string) => {
  const response = await api.post<{ success: boolean; labels: BackgroundAiTagLabels }>(
    '/background-ai/names/translate',
    { sourceLang, text },
  );
  return response.data;
};

export const translateBackgroundAiTag = async (vi: string) => {
  const response = await api.post<{ success: boolean; labels: BackgroundAiTagLabels }>(
    '/background-ai/tags/translate',
    { vi },
  );
  return response.data;
};

export const createBackgroundAiTag = async (labels: BackgroundAiTagLabels, adminUserId?: string) => {
  const path = adminUserId
    ? `/background-ai/admin/users/${adminUserId}/tags`
    : '/background-ai/tags';
  const response = await api.post<{ success: boolean; tags: BackgroundAiTag[] }>(path, { labels });
  return response.data;
};

export const deleteBackgroundAiTag = async (key: string, adminUserId?: string) => {
  const path = adminUserId
    ? `/background-ai/admin/users/${adminUserId}/tags/${encodeURIComponent(key)}`
    : `/background-ai/tags/${encodeURIComponent(key)}`;
  const response = await api.delete<{ success: boolean; tags: BackgroundAiTag[] }>(path);
  return response.data;
};

export const getAdminUserBackgroundAiTags = async (userId: string) => {
  const response = await api.get<{ success: boolean; tags: BackgroundAiTag[] }>(
    `/background-ai/admin/users/${userId}/tags`,
  );
  return response.data;
};

export const getMyBackgroundAiThemes = async () => {
  const response = await api.get<{ success: boolean; themes: BackgroundAiTheme[] }>('/background-ai/themes');
  return response.data;
};

export const createMyBackgroundAiTheme = async (data: {
  name: string;
  sourceLang?: string;
  nameLabels?: BackgroundAiTagLabels;
  description?: string;
  image?: File;
  proEnabled?: boolean;
  liteEnabled?: boolean;
  proMachineCodes?: string[];
  liteMachineCodes?: string[];
  scheduleEnabled?: boolean;
  availableFrom?: string | null;
  availableUntil?: string | null;
}) => {
  const response = await api.post<{ success: boolean; theme: BackgroundAiTheme }>(
    '/background-ai/themes',
    themeForm(data),
  );
  return response.data;
};

export const updateMyBackgroundAiTheme = async (
  themeId: string,
  data: {
    name: string;
    sourceLang?: string;
    nameLabels?: BackgroundAiTagLabels;
    description?: string;
    image?: File;
    proEnabled?: boolean;
    liteEnabled?: boolean;
    proMachineCodes?: string[];
    liteMachineCodes?: string[];
    scheduleEnabled?: boolean;
    availableFrom?: string | null;
    availableUntil?: string | null;
  },
) => {
  const response = await api.put<{ success: boolean; theme: BackgroundAiTheme }>(
    `/background-ai/themes/${themeId}`,
    themeForm(data),
  );
  return response.data;
};

export const deleteMyBackgroundAiTheme = async (themeId: string) => {
  const response = await api.delete<{ success: boolean }>(`/background-ai/themes/${themeId}`);
  return response.data;
};

export const createMyBackgroundAiPreset = async (
  themeId: string,
  data: { name: string; sourceLang?: string; nameLabels?: BackgroundAiTagLabels; prompt: string; tags?: string[]; image?: File; referenceImage?: File; clearReference?: boolean },
) => {
  const response = await api.post<{ success: boolean; preset: BackgroundAiPreset }>(
    `/background-ai/themes/${themeId}/presets`,
    presetForm(data),
  );
  return response.data;
};

export const updateMyBackgroundAiPreset = async (
  themeId: string,
  presetId: string,
  data: { name: string; sourceLang?: string; nameLabels?: BackgroundAiTagLabels; prompt: string; tags?: string[]; image?: File; referenceImage?: File; clearReference?: boolean },
) => {
  const response = await api.put<{ success: boolean; preset: BackgroundAiPreset }>(
    `/background-ai/themes/${themeId}/presets/${presetId}`,
    presetForm(data),
  );
  return response.data;
};

export const deleteMyBackgroundAiPreset = async (themeId: string, presetId: string) => {
  const response = await api.delete<{ success: boolean }>(
    `/background-ai/themes/${themeId}/presets/${presetId}`,
  );
  return response.data;
};

export const getAdminUserBackgroundAiThemes = async (userId: string) => {
  const response = await api.get<{ success: boolean; themes: BackgroundAiTheme[] }>(
    `/background-ai/admin/users/${userId}/themes`,
  );
  return response.data;
};

export const createAdminUserBackgroundAiTheme = async (
  userId: string,
  data: {
    name: string;
    sourceLang?: string;
    nameLabels?: BackgroundAiTagLabels;
    description?: string;
    image?: File;
    proEnabled?: boolean;
    liteEnabled?: boolean;
    proMachineCodes?: string[];
    liteMachineCodes?: string[];
    scheduleEnabled?: boolean;
    availableFrom?: string | null;
    availableUntil?: string | null;
  },
) => {
  const response = await api.post<{ success: boolean; theme: BackgroundAiTheme }>(
    `/background-ai/admin/users/${userId}/themes`,
    themeForm(data),
  );
  return response.data;
};

export const updateAdminUserBackgroundAiTheme = async (
  userId: string,
  themeId: string,
  data: {
    name: string;
    sourceLang?: string;
    nameLabels?: BackgroundAiTagLabels;
    description?: string;
    image?: File;
    proEnabled?: boolean;
    liteEnabled?: boolean;
    proMachineCodes?: string[];
    liteMachineCodes?: string[];
    scheduleEnabled?: boolean;
    availableFrom?: string | null;
    availableUntil?: string | null;
  },
) => {
  const response = await api.put<{ success: boolean; theme: BackgroundAiTheme }>(
    `/background-ai/admin/users/${userId}/themes/${themeId}`,
    themeForm(data),
  );
  return response.data;
};

export const deleteAdminUserBackgroundAiTheme = async (userId: string, themeId: string) => {
  const response = await api.delete<{ success: boolean }>(
    `/background-ai/admin/users/${userId}/themes/${themeId}`,
  );
  return response.data;
};

export const createAdminUserBackgroundAiPreset = async (
  userId: string,
  themeId: string,
  data: { name: string; sourceLang?: string; nameLabels?: BackgroundAiTagLabels; prompt: string; tags?: string[]; image?: File; referenceImage?: File; clearReference?: boolean },
) => {
  const response = await api.post<{ success: boolean; preset: BackgroundAiPreset }>(
    `/background-ai/admin/users/${userId}/themes/${themeId}/presets`,
    presetForm(data),
  );
  return response.data;
};

export const updateAdminUserBackgroundAiPreset = async (
  userId: string,
  themeId: string,
  presetId: string,
  data: { name: string; sourceLang?: string; nameLabels?: BackgroundAiTagLabels; prompt: string; tags?: string[]; image?: File; referenceImage?: File; clearReference?: boolean },
) => {
  const response = await api.put<{ success: boolean; preset: BackgroundAiPreset }>(
    `/background-ai/admin/users/${userId}/themes/${themeId}/presets/${presetId}`,
    presetForm(data),
  );
  return response.data;
};

export const deleteAdminUserBackgroundAiPreset = async (
  userId: string,
  themeId: string,
  presetId: string,
) => {
  const response = await api.delete<{ success: boolean }>(
    `/background-ai/admin/users/${userId}/themes/${themeId}/presets/${presetId}`,
  );
  return response.data;
};

export const toggleBackgroundAiUser = async (userId: string, backgroundAiEnabled: boolean) => {
  const response = await api.put<{
    success: boolean;
    userId: string;
    backgroundAiEnabled: boolean;
  }>(`/background-ai/admin/users/${userId}`, { backgroundAiEnabled });
  return response.data;
};

export const updateBackgroundAiAccountGeneration = async (
  userId: string,
  data: {
    provider: BackgroundAiProviderId;
    quality: BackgroundAiQuality;
    model: string;
    quotaLimit: number | null;
  }
) => {
  const response = await api.put<{
    success: boolean;
    userId: string;
    backgroundAiProvider: BackgroundAiProviderId;
    backgroundAiQuality: BackgroundAiQuality;
    backgroundAiFalModel: string;
    backgroundAiPiapiModel: string;
    quotaLimit: number | null;
    quotaUsed: number;
    quotaRemaining: number | null;
  }>(`/background-ai/admin/users/${userId}/generation`, data);
  return response.data;
};

export const toggleBackgroundAiMultiSlot = async (userId: string, backgroundAiMultiSlotEnabled: boolean) => {
  const response = await api.put<{
    success: boolean;
    userId: string;
    backgroundAiMultiSlotEnabled: boolean;
  }>(`/background-ai/admin/users/${userId}/multi-slot`, { backgroundAiMultiSlotEnabled });
  return response.data;
};

export default api;
 